import React from "react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "../components/ui";

const sections = [
  {
    title: "Meeting in Person",
    items: [
      "**Meet in public:** Always meet in a public place for the first few dates (e.g., a busy café or the Max & Sherry venue).",
      "**Tell a friend:** Let a trusted friend or family member know where you are going, who you are meeting, and when you expect to be back.",
      "**Stay sober:** Avoid excessive alcohol or drugs so you can stay in control and alert.",
      "**Arrange your own transport:** Do not rely on your date for a ride home.",
    ],
  },
  {
    title: "Protecting Your Personal Information",
    items: [
      "Never share your home address, workplace, or financial details with someone you just met.",
      "Keep conversations within the ConnectPlus platform until you trust the person.",
      "Never send money to someone you haven't met in person, regardless of the story they tell.",
    ],
  },
  {
    title: "Reporting and Blocking",
    items: [
      "If someone makes you feel uncomfortable, trust your instincts and end the interaction.",
      "Use the **Block** feature immediately. This prevents them from seeing your profile or messaging you.",
      "**Report** any user who violates our Community Guidelines. Our moderation team reviews all reports.",
    ],
  },
  {
    title: "Account Security",
    items: [
      "Use a strong, unique password for your account.",
      "Never share your OTP (One Time Password) with anyone.",
      "We will never ask for your mobile money PIN or bank credentials.",
    ],
  },
];

// Simple bolding parser for **text**
function renderItem(item: string) {
  return item.split(/\*\*(.*?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="text-black font-semibold">
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

export default function SafetyCentre() {
  return (
    <main className="bg-white px-[5%] pt-[140px] pb-20 lg:pb-[100px]">
      <div className="max-w-[1100px] mx-auto">
        <Reveal>
          <Eyebrow>Trust & Safety</Eyebrow>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="text-[clamp(28px,4vw,44px)] font-bold tracking-[-1.2px] mb-4">
            Safety Centre
          </h1>
        </Reveal>
        <Reveal delay={150}>
          <p className="text-[15px] text-muted mb-12 max-w-[620px] leading-relaxed">
            Your safety is our highest priority. Max & Sherry ConnectPlus is
            built to foster genuine and safe connections, but keeping the
            community safe is a shared responsibility.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {sections.map((s, i) => (
            <Reveal key={s.title} delay={200 + i * 50} className="h-full">
              <div className="h-full p-6 lg:p-8 rounded-[20px] bg-blush border border-rose/10">
                <h2 className="text-xl font-bold tracking-tight mb-4">
                  {s.title}
                </h2>
                <ul className="space-y-3">
                  {s.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-[15px] text-muted leading-relaxed"
                    >
                      <span className="text-rose mt-0.5">•</span>
                      <span>{renderItem(item)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={450}>
          <div className="mt-10 p-6 lg:p-8 rounded-[20px] bg-black text-white flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <h2 className="text-lg font-bold tracking-tight mb-1">
                Need to report someone?
              </h2>
              <p className="text-[14px] text-white/60">
                Our safety team reviews every report.
              </p>
            </div>
            <Link
              to="/contact"
              className="self-start md:self-auto bg-white text-black text-sm font-medium px-5 py-2.5 rounded-full hover:bg-neutral-200 transition-colors duration-200 no-underline whitespace-nowrap"
            >
              Contact the safety team
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
